/**
 * Sealed channel-to-cipher index.
 *
 * Channel ids are stored only as ciphertext under the alias wrapping key, so
 * an IndexedDB export does not reveal which channels a cipher opens. The
 * in-memory map (channel id to local cipher id) is built once at unlock.
 */

import { decrypt, encrypt, fromBase64, toBase64, type CryptoProfile } from '@adieuu/crypto';

export interface EncryptedChannelIds {
  ciphertext: string;
  nonce: string;
}

function channelIdAad(localCipherId: string): Uint8Array {
  return new TextEncoder().encode(localCipherId);
}

/** Seal a cipher's channel list. The local cipher id is bound as associated data. */
export function sealChannelIds(
  channelIds: readonly string[],
  wrappingKey: Uint8Array,
  localCipherId: string,
  profile: CryptoProfile = 'default',
): EncryptedChannelIds {
  const unique = [...new Set(channelIds.filter((id) => id.length > 0))];
  const plaintext = new TextEncoder().encode(JSON.stringify(unique));
  const sealed = encrypt(wrappingKey, plaintext, profile, undefined, channelIdAad(localCipherId));
  return {
    ciphertext: toBase64(sealed.ciphertext),
    nonce: toBase64(sealed.nonce),
  };
}

/** Open a sealed channel list. Throws when the wrapping key or cipher id does not match. */
export function openChannelIds(
  sealed: EncryptedChannelIds,
  wrappingKey: Uint8Array,
  localCipherId: string,
  profile: CryptoProfile = 'default',
): string[] {
  const plaintext = decrypt(
    wrappingKey,
    fromBase64(sealed.ciphertext),
    fromBase64(sealed.nonce),
    profile,
    channelIdAad(localCipherId),
  );
  const parsed = JSON.parse(new TextDecoder().decode(plaintext)) as unknown;
  if (!Array.isArray(parsed)) return [];
  return [...new Set(parsed.filter((id): id is string => typeof id === 'string' && id.length > 0))];
}
